import {
  cancel,
  take,
  takeEvery,
  put,
  fork,
  spawn,
  call,
  getContext,
  select,
  race,
  delay,
  takeLatest,
  actionChannel,
  putResolve,
} from 'redux-saga/effects';
import { eventChannel, buffers } from 'redux-saga';
import socketIO from 'socket.io-client';
import _omit from 'lodash/omit';

import { getLogger } from '../../logger/index';
import { getLocale } from '../intl/selectors';
import selectors from './selectors';

import {
  SOCKET_CONNECT,
  SOCKET_CONNECT_ANONYMOUS,
  SOCKET_CONNECT_AUTH_CUSTOMER,
  SOCKET_DISCONNECT,
  SOCKET_EMIT,
  SEND_REVIEW,
  socketConnect,
  socketConnectSuccess,
  socketConnectFailure,
  socketConnected,
  socketDisconnected,
  socketEvent,
  socketDisconnect,
  setCustomerChat,
  initiateReview,
  socketConnectCancel,
  sendReviewSuccess,
  sendReviewFailure,
} from './actions';
import authSelectors from '../auth/selectors';
import { dispatchActionIfSelectorHasValue } from '../../utils/saga';
import chatSelectors from '../chat/selectors';
import chatActions, { CHECK_CUSTOMER_AGENT_DONE, SET_CUSTOMER_AGENT } from '../chat/actions';
import { ensureAuthNotPending, callForCustomer, callForEmployee } from '../auth/sagaUtils';

const CHECK_YOUR_COOKIES = 'PLEASE_CHECK_YOUR_HTTP_ONLY_COOKIES';

const logger = getLogger('communication');

let connecting = false;

function createSocketChannel(socket) {
  logger.debug('Setup chat socket channel');
  return eventChannel(emit => {
    const { onevent } = socket;
    // eslint-disable-next-line no-param-reassign
    socket.onevent = packet => {
      logger.debug('Event packet', packet);
      const args = packet.data || [];
      onevent.call(socket, packet); // original call
      onevent.call(socket, { ...packet, data: ['*'].concat(args) }); // additional call to catch-all
    };

    const errorCallback = error => {
      logger.error('socket.on error', error);
      emit({ name: 'Error', error });
    };

    const errorConnectCallback = error => {
      logger.error('errorConnectCallback', error);
      emit({ name: 'ConnectError', error });
    };

    const errorReconnectCallback = error => {
      logger.error('errorReconnectCallback', error);
      emit({ name: 'ReconnectConnectError', error });
      logger.debug('Gracefully disconnect socket and reconnect');
      emit({ name: 'Disconnected', reconnect: true });
    };

    const connectCallback = () => {
      logger.debug('Socket IO connection established.');
      emit({ name: 'Connected' });
      connecting = false;
    };

    const reconnectAttemptCallback = attemptNumber => {
      logger.debug('Socket IO reconnection attempted. Attempt number:', attemptNumber);
      emit({ name: 'ReconnectAttempted', attemptNumber });
    };

    const reconnectCallback = attemptNumber => {
      logger.debug('Socket IO reconnection established. Attempt number:', attemptNumber);
      emit({ name: 'Reconnected', attemptNumber });
    };

    const reconnectingCallback = attemptNumber => {
      logger.debug('Socket IO reconnecting. Attempt number:', attemptNumber);
      emit({ name: 'Reconnecting', attemptNumber });
    };

    const disconnectCallback = reason => {
      logger.debug('Socket IO connection disconnected. Reason:', reason);
      // the disconnection was initiated by the server, you need to reconnect manually
      emit({ name: 'Disconnected', reconnect: true });
    };

    const dispatchEvent = (eventName, args) => {
      logger.debug('Incoming:', eventName, '- Args:', args);
      emit({ name: 'SocketEvent', eventName, args });
    };

    const reconnectFailedCallback = e => {
      logger.error('Reconnect failed', e);
    };

    socket.on('error', errorCallback);
    socket.on('connect_error', errorConnectCallback);
    socket.on('connect', connectCallback);
    socket.on('disconnect', disconnectCallback);
    socket.on('reconnect_failed', errorReconnectCallback);
    socket.on('reconnect_error', reconnectFailedCallback);
    socket.on('reconnect', reconnectCallback);
    socket.on('reconnecting', reconnectingCallback);
    socket.on('reconnect_attempt', reconnectAttemptCallback);
    socket.on('*', dispatchEvent);

    logger.debug('Socket channel established.');
    return () => {
      // Finalize
      logger.info('Socket channel closed');
    };
  });
}

function* watchSocketEventsSaga(context) {
  // eslint-disable-next-line no-param-reassign
  context.active = true;
  while (context.active) {
    logger.debug('Awaiting socket channel event.');
    const event = yield take(context.channel);
    logger.debug('Socket channel event:', event);
    switch (event.name) {
      case 'SocketEvent':
        logger.debug('WS_EVENT', event);
        yield put(socketEvent(_omit(event, 'name')));
        break;
      case 'Connected':
        yield put(socketConnected());
        break;
      case 'Disconnected':
        yield put(socketDisconnect());
        // eslint-disable-next-line no-case-declarations
        const isEmployee = yield select(authSelectors.isEmployee);
        if (event.reconnect && isEmployee) {
          logger.debug('Try to reconnect.');
          yield call(
            dispatchActionIfSelectorHasValue,
            socketConnect(context.options),
            selectors.socket.isConnected,
            false,
            10,
            100
          );
        }
        break;
      case 'Error':
        logger.error('Socket error', event.error);
        break;
      case 'ConnectError':
        logger.error('Socket connection error', event.error);
        yield put(socketConnectFailure(event.error));
        break;
    }
  }
  logger.debug('Left socket channel event loop.');
}

function* checkAgentSaga() {
  try {
    const agent = yield select(chatSelectors.getAgent);
    let isAgentCheckPending = yield select(chatSelectors.isAgentCheckPending);
    if (!agent && !isAgentCheckPending) {
      logger.debug('Trigger checkCustomerAgent');
      yield putResolve(chatActions.checkCustomerAgent());
    }

    isAgentCheckPending = yield select(chatSelectors.isAgentCheckPending);
    if (isAgentCheckPending) {
      logger.debug('Wait for agent');
      yield take(SET_CUSTOMER_AGENT);
    }
  } catch (e) {
    logger.error(e);
  }
}

function* waitForAgent() {
  try {
    const isAgentCheckPending = yield select(chatSelectors.isAgentCheckPending);

    if (isAgentCheckPending) {
      logger.debug('Wait for agent');
      yield take(SET_CUSTOMER_AGENT);
    }

    return yield select(chatSelectors.getAgent);
  } catch (e) {
    logger.error(e);
  }
}

function* socketConnectCustomerSaga(emitChannel) {
  try {
    logger.debug('Ensure customer has agent');
    yield call(checkAgentSaga);
    const agent = yield select(chatSelectors.getAgent);
    if (!agent) {
      return;
    }

    logger.debug('Ensure auth not pending');
    yield ensureAuthNotPending();
    logger.debug('Connect socket by customer');
    yield call(socketConnectSaga, emitChannel);
  } catch (e) {
    logger.error(e);
  }
}

function* socketConnectSaga(emitChannel, action) {
  const { options = {} } = action && action.payload ? action.payload : {};
  const agent = yield call(waitForAgent);
  const isCustomer = yield select(authSelectors.isCustomer);
  logger.debug('Connect to socket requested.');
  const connected = yield select(selectors.socket.isConnected);
  if (connected) {
    logger.debug('Connection ignored, already connected');
    yield put(socketConnectCancel());
    return;
  }
  if (connecting) {
    logger.debug('Connection ignored, already connecting');
    return;
  }
  if (isCustomer && !agent) {
    logger.debug('Connection ignored, no agent available. 1');
    yield put(socketConnectCancel());
    return;
  }
  try {
    connecting = true;
    logger.info('Initializing socket IO');
    const http = yield getContext('http');
    const canUseAudioCall = yield getContext('canUseAudioCall');
    const canUseVideoCall = yield getContext('canUseVideoCall');
    const locale = yield select(getLocale);
    const isAudioCallAvailable = yield call(canUseAudioCall);
    const isVideoCallAvailable = yield call(canUseVideoCall);
    const isEmployee = yield select(authSelectors.isEmployee);
    const isAnonymous = yield select(selectors.isAnonymous);
    const anonymous = yield select(selectors.getAnonymous);
    let accessToken = yield select(selectors.socket.token);
    let tokenExpires = yield select(selectors.socket.tokenExpires);

    const voipDeviceToken = yield call(getForVoipToken);
    logger.debug('voipDeviceToken', voipDeviceToken);
    const agent = yield select(chatSelectors.getAgent);

    if (!isEmployee && !agent) {
      logger.debug('Connection ignored, no agent available. 2');
      yield put(socketConnectCancel());
      return;
    }
    if (!accessToken || !tokenExpires || (tokenExpires && new Date(tokenExpires).getTime() <= Date.now())) {
      if (isAnonymous) {
        logger.debug('Requesting anonymous socket IO connection.');

        const requestUrl = `/api/customerchats.json?&_locale=${locale}`;
        const requestConfig = {};
        const requestPayload = { agent: agent.id, email: anonymous.email, name: anonymous.name };

        const response = yield call(http.post, requestUrl, requestPayload, requestConfig);
        if (response.status < 200 || response.status > 299) {
          logger.error('Response with HTTP status', response.status, response);
          yield put(socketConnectFailure(response.data));
        } else {
          const { token: newAccessToken, isAnonymousUser, ...anonymousUserData } = response.data;
          const newTokenExpires = Date.now() + 60 * 1000 * 60 * 2;
          logger.debug('Setting up anonymous socket IO connection.');
          yield put(socketConnectSuccess(newAccessToken || CHECK_YOUR_COOKIES, newTokenExpires, anonymousUserData));
          yield put(setCustomerChat(response.data));
          accessToken = newAccessToken;
          tokenExpires = newTokenExpires;
        }
      } else if (!isEmployee) {
        logger.debug('Requesting customer socket IO connection.');
        const agent = yield select(chatSelectors.getAgent);

        const requestUrl = `/api/customerchats.json?&_locale=${locale}`;
        const requestConfig = {};
        // const agent = yield select(chatSelectors.getAgent);

        const response = yield call(http.post, requestUrl, { agent: agent.id }, requestConfig);
        if (response.status < 200 || response.status > 299) {
          logger.error('Response with HTTP status', response.status, response);
          yield put(socketConnectFailure(response.data));
        } else {
          const { token: newAccessToken } = response.data;
          const newTokenExpires = Date.now() + 60 * 1000 * 60 * 2;
          logger.debug('Setting up customer socket IO connection.');
          yield put(socketConnectSuccess(newAccessToken || CHECK_YOUR_COOKIES, newTokenExpires, null));
          yield put(setCustomerChat(response.data));
          accessToken = newAccessToken;
          tokenExpires = newTokenExpires;
        }
      } else {
        logger.debug('Requesting socket IO connection.');
        const requestUrl = `/api/chatterinit.json?&_locale=${locale}`;
        const requestConfig = {};

        const response = yield call(http.get, requestUrl, requestConfig);
        if (response.status < 200 || response.status > 299) {
          logger.error('Response with HTTP status', response.status, response);
          yield put(socketConnectFailure(response.data));
        } else {
          const { access_token: newAccessToken, exp: newTokenExpires } = response.data;
          logger.debug('Setting up socket IO connection.');
          yield put(socketConnectSuccess(newAccessToken || CHECK_YOUR_COOKIES, newTokenExpires, null));
          accessToken = newAccessToken;
          tokenExpires = newTokenExpires;
        }
      }
    }

    const isAgent = yield select(selectors.isAgent);
    const baseUrlForSocket = http.defaults.baseURL.replace('/app_dev.php', '');
    const isMobile = yield getContext('isMobile');
    const query = {
      ...(options.query || {}),
      isAgent,
      isAudioCallAvailable,
      isVideoCallAvailable,
      isMobile: isMobile,
    };
    if (voipDeviceToken != null) {
      query.voipDeviceToken = voipDeviceToken;
    }
    if (accessToken !== CHECK_YOUR_COOKIES) {
      query.token = accessToken;
    }
    const socket = socketIO(`${baseUrlForSocket}/chatter`, {
      path: '/chatter/socket',
      reconnection: false,
      query,
    });
    const channel = yield call(createSocketChannel, socket);
    const socketContext = {
      socket,
      active: false,
      channel,
      options,
    };
    const socketEmitWatcher = yield fork(watchEmitSaga, socketContext, emitChannel);
    yield spawn(watchSocketEventsSaga, socketContext);
    try {
      logger.debug('Watch for chat socket disconnect');
      yield take(SOCKET_DISCONNECT); // Wait for disconnect by purpose
      logger.debug('Socket disconnection by purpose.');
    } catch (error) {
      logger.error('Waiting for error', error);
    } finally {
      socketContext.active = false; // End socket channel loop
      try {
        logger.debug('Close Socket_IO connection');
        // yield apply(socket, socket.close);
        socket.close();
      } catch (error) {
        logger.error('Error closing socket', error);
      }
      yield cancel(socketEmitWatcher);
      try {
        yield call(channel.close);
      } catch (error) {
        logger.error('Error closing socket channel', error);
      }
      logger.debug('Cancel socket action watchers.');
    }
  } catch (err) {
    logger.error('Exception while connecting to socket', err);
    yield put(socketConnectFailure(err));
  } finally {
    connecting = false;
    yield put(socketDisconnected());
  }
}

function* socketEmitSaga(socket, { payload: { eventName, args, callback } }) {
  try {
    logger.debug('Socket channel emit event:', eventName, '- Args:', args);
    yield call([socket, 'emit'], eventName, args, callback);
  } catch (e) {
    logger.error('Socket emit', e);
  }
}

function* watchEmitSaga(context, emitChannel) {
  const { socket } = context;
  logger.debug('Watch socket emit channel', emitChannel);
  yield takeEvery(emitChannel, socketEmitSaga, socket);
}

function* handleLogoutSaga() {
  logger.debug('Logout received, disconnect socket for user.');
  yield put(socketDisconnect());
}

function* handleChatEndedSaga() {
  const agent = yield select(chatSelectors.getAgent);
  const username = agent.id;
  const conversation = yield select(chatSelectors.getConversationByUser(username));
  const customerChat = yield select(selectors.getCustomerChat);
  yield put(
    initiateReview({
      agent,
      conversation,
      customerChatId: customerChat.customerChat,
      customerVideoCallId: customerChat.customerVideoCall,
    })
  );
  logger.debug('Chat ended received, disconnect socket for end customer or anonymous user.');
  yield put(socketDisconnect());
}

function* getForVoipToken() {
  const voipDeviceToken = yield select(authSelectors.getVoipDeviceToken);
  if (voipDeviceToken) {
    return voipDeviceToken;
  }

  const { noToken } = yield race({
    voipDeviceToken: take('STORE_VOIP_DEVICE_TOKEN'),
    noToken: delay(5000),
  });

  if (noToken) {
    return;
  }
  return yield select(authSelectors.getVoipDeviceToken);
}

/*
{"overallStars":5,"remarks":"War gudd","connectionQualityStars":5}
*/
function* sendReviewDataSaga({ payload }) {
  try {
    const http = yield getContext('http');
    const locale = yield select(getLocale);
    const reviewData = yield select(selectors.getReviewData);

    if (reviewData && (reviewData.customerChatId || reviewData.customerVideoCallId)) {
      const requestUrl = `/api/customerchats/${reviewData.customerChatId}.json?&_locale=${locale}`;
      const requestConfig = {};

      const response = yield call(http.put, requestUrl, payload, requestConfig);
      if (response.status < 200 || response.status > 299) {
        logger.error('Response with HTTP status', response.status, response);
        yield put(sendReviewFailure(response.data));
      } else {
        yield put(sendReviewSuccess(response.data));
      }
    } else {
      throw new Error('Missing review data');
    }
  } catch (err) {
    logger.error('Error sending review data', err);
    yield put(sendReviewFailure(err));
  }
}

export function* watchCommunicationActions() {
  try {
    logger.debug('Watch SOCKET_EMIT action in buffered channel');
    const emitChannel = yield actionChannel(SOCKET_EMIT, buffers.expanding(10));

    logger.debug('Setup saga watcher');
    yield takeEvery('FETCH_CREDENTIALS_FULFILLED', function*() {
      yield callForEmployee(socketConnectSaga, emitChannel);
    });
    yield takeEvery(SOCKET_CONNECT, socketConnectSaga, emitChannel);
    yield takeLatest(SOCKET_CONNECT_AUTH_CUSTOMER, socketConnectCustomerSaga, emitChannel);
    yield takeEvery(SOCKET_CONNECT_ANONYMOUS, socketConnectCustomerSaga, emitChannel);
    yield takeEvery('HANDLE_LOGIN_FULFILLED', function*() {
      const isEmployee = yield select(authSelectors.isEmployee);
      if (!isEmployee) {
        logger.debug('Login customer received, disconnect socket.');
        yield put(socketDisconnect());
      } else {
        logger.debug('Login employee received, connect socket.');
        yield call(socketConnectSaga, emitChannel);
      }
    });
    yield takeEvery('HANDLE_LOGOUT_FULFILLED', handleLogoutSaga);
    // for customer
    yield takeEvery(
      action => action.type === 'SOCKET_EVENT' && action.payload.eventName === 'chat_ended',
      function*(action) {
        yield callForCustomer(handleChatEndedSaga, action.payload.args);
      }
    );
    // for customer
    yield takeEvery(SEND_REVIEW, sendReviewDataSaga);
  } catch (e) {
    logger.error('watchCommunicationActions', e);
  }
}

export default [watchCommunicationActions];
